/*
Helper functions used for DutyStatusLogs
This is for performance reasons as setting up Backbone models is expensive and very noticable on slower devices
*/
import { getServerDate } from "../utils/storage/date";
import {
    dstCalculator,
    getDateTokenString,
    getLocalDaySpan,
    getStartOfDay,
    getStartOfDayUserTimezone,
    IReportPeriod,
    toDateTime
} from "../utils/date";
import { ValidationErrorLogger } from "../utils/validator";
import Translator from "../utils/translator";
import { BaseCache, makeDeviceCache } from "../utils/storage/cache";
import DutyStatusLog, {ALL_HOS_STATUSES, ENGINE_STATUSES, HOS_STANDARD_STATUSES, IAnnotation, REQUESTED, SITUATIONAL_DRIVING_CLEAR_STATUS, STANDARD_STATUSES } from "./DutyStatusLog";
import { extend, isEqual } from "../utils/vanilla";
import { CancelablePromise as Promise } from "../utils/cancelablePromise";
import idSerializer from "geotab-uuid";
import HosProcess from "../services/hosProcess";
import Sync from "../sync/sync";
import { HosMainModel } from "../ui/hos/tabs/main/main";
import constants from "../constants";
import { getCustomExemptHOSName } from "../utils/hosHelpers";
import { DriveStore } from "../store/reducers";
import { UserService } from "../utils/users";
import { HosLogActions } from "../store/hosLogs/actions";
import { IHistoricalDayEntry } from "../store/hosLogs/types";
import * as logger from "../utils/logger";
import { Device } from "./Device";
import { IFeatureFlagsService } from "../services/featureFlags";
import { FeatureFlagName } from "../utils/featureFlagName";
import {
    getPreviousOrNextByDate, isSituationalDrivingStatus, SITUATIONAL_DRIVING_CLEAR, CANADARULESETS, compareDatesWithSequenceIds,
    isStatusLog, roundBasedOnStatus, type THosStatus, type THosOrigin, drivingWhileInExemptionStatuses
} from "hos-tools";
import { isDateTimeInPreviousDwieRange } from "../ui/hos/tabs/logs/editPcOrYmUtils";

const NO_USER_ID = "NoUserId";
const ALLOWED_STATUS_TO_BE_PAIRED_WITH_AUTO_D = ["ON", "OFF", "SB"];

export const SDC_DIFFERENCE_FROM_LOG = 10;
export const DSWIE_DIFFERENCE_FROM_LOG = 20;

export const NO_DEVICE_ID = "NoDeviceId";

export interface IDailyOdometer {
    [key: string]: {
        [deviceId: string]: IDayOdometer;
    };
}

export interface INewAnnotation {
    comment: string;
}

export interface IDayOdometer {
    start: number;
    end: number;
    endEngineHours: number;
}

export interface IPCLimitDetails {
    latestPCLog: DutyStatusLog | null;
    isPCLimitExceeded: boolean;
}

export interface IExemptionLogEditRequest {
    editedExemptionLog: DutyStatusLog | null;
    logsToEdit: DutyStatusLog[];
    logsToRemove: DutyStatusLog[];
}

export class DutyStatusLogService {
    private readonly deviceCache: BaseCache<IDevice | Device>;

    constructor(
        private readonly store: DriveStore,
        private readonly userService: UserService,
        private readonly featureFlagsService: IFeatureFlagsService
    ) {
        this.deviceCache = makeDeviceCache(store);
    }

    isAnyLogUnassignedForWorkflow(hosProcess: HosProcess): boolean {
        const logs = this.getSortedHosLogs(this.getHOSLogs(false).filter(log =>
                        log.isUnassignedPageLog()
                        && !log.requestedEdit
                        && log.getDate().getTime() >= hosProcess.getRetentionStartDate().getTime()));

        return logs.length > 0 && this.getAllPairedLogs(logs).length > 0;
    }

    isAnyLogUnverifiedForWorkflow (hosProcess: HosProcess, isLogout: boolean, user?: DeepReadonlyObject<IUser>, endDate?: Date): boolean {
        let currentUser = user ?? this.userService.getCurrentUser();
        if (!currentUser) {
            return false;
        }
        let logCollection = this.getSortedHosLogs(this.getHOSLogs(false, currentUser.id).filter(hosLog => hosLog.isMainLogPageLog(currentUser)));
        const [requestedAddShortenYMPCLogIdSet, filteredLogs] = this.findModifiedExemptionLogsAndFilterRequestedAdds(logCollection);
        let dateRange = {
            startDate: hosProcess.getRetentionStartDate(2),
            endDate: !isLogout ? (endDate ?? getStartOfDayUserTimezone(getServerDate())) : constants.MAX_DATE
        };
        return !!Object.keys(this.getUnverifiedLocalDays(filteredLogs, dateRange, requestedAddShortenYMPCLogIdSet)).length;
    }

// eslint-disable-next-line complexity
    convertDutyStatusLogsFromServer(dutyStatusLogs: any[], currentUser: IUser | null = null): DutyStatusLog[] {
        let activeLogs: DutyStatusLog[] = [];
        let dslog: any;
        let currentLog: any | null;
        let requestedEdit: any | null;
        let inactiveFallback: any | null;
        let logsByParentId: DutyStatusLog[];
        let parentId: string;
        let logsByParentIds: IIdDictionary<DutyStatusLog[]> = {};
        let containsLog = (list: DutyStatusLog[], log: DutyStatusLog) => list.some((logFromList: DutyStatusLog) => logFromList.id === log.id);

        if (dutyStatusLogs) {
            for (dslog of dutyStatusLogs) {
                if (dslog.driver === NO_USER_ID) {
                    // Convert NoUserId from server to IIdEntity
                    dslog.driver = { id: NO_USER_ID };
                }
                if (dslog.device === NO_DEVICE_ID) {
                    // Convert NoDeviceId from server to IIdEntity
                    dslog.device = { id: NO_DEVICE_ID };
                }
                parentId = dslog.parentId || dslog.id;
                if (!logsByParentIds.hasOwnProperty(parentId)) {
                    logsByParentIds[parentId] = [dslog];
                } else if (!containsLog(logsByParentIds[parentId], dslog)) {
                    logsByParentIds[parentId].push(dslog);
                }
                if (dslog.state === "Requested" && dslog?.isTransitioning) {
                    dslog.state = "Transitioning";
                }
            }

            for (parentId in logsByParentIds) {
                logsByParentId = logsByParentIds[parentId];
                logsByParentId.sort((a, b) => parseInt(b.version as string, 16) - parseInt(a.version as string, 16));
                currentLog = null;
                requestedEdit = null;
                inactiveFallback = null;

                let createdDate = undefined;
                for (dslog of logsByParentId) {
                    if (dslog.state === "Requested" || dslog.state === "Transitioning") {
                        requestedEdit = dslog;
                    } else if (dslog.state === "Inactive" && !inactiveFallback) {
                        inactiveFallback = dslog;
                    } else if (dslog.state !== "Rejected" && dslog.state !== "Inactive" && !currentLog) {
                        currentLog = dslog;
                    }
                    if (dslog.id === dslog.parentId) {
                        createdDate = dslog.editDateTime;
                    }
                }

                // A retired row's version can exceed its Active replacement's (the server's
                // row-version trigger bumps version on retirement too, not just on insert), so
                // an Active row must win regardless of version order. Keep only the first
                // (highest-version) Inactive row as a fallback; later Inactive rows must not
                // populate currentLog while an Active row may still follow. The fallback still
                // propagates genuine deletions when no Active row exists.
                currentLog = currentLog || inactiveFallback;

                if (currentLog) {
                    currentLog.id = parentId;
                    currentLog._createdDate = createdDate;
                    if (requestedEdit) {
                        currentLog.requestedEdit = requestedEdit;
                    }
                } else if (requestedEdit) {
                    currentLog = requestedEdit;
                    currentLog.id = currentLog.parentId;
                    currentLog._createdDate = createdDate;
                    currentLog.requestedAdd = true;
                }

                if (currentLog?.requestedEdit?.state === "Transitioning" && currentLog.requestedEdit.driver.id !== currentUser?.id) {
                    currentLog._awaitingTransition = true;
                }

                if (currentLog &&
                    (currentUser === null || currentLog.driver.id === currentUser.id
                        || (currentLog.requestedEdit && currentLog.requestedEdit.state === "Transitioning")
                        || (currentLog.driver.id === "NoUserId" && requestedEdit && requestedEdit.driver.id === currentUser.id))) {
                    activeLogs.push(new DutyStatusLog(currentLog));
                }
            }
        }
        return activeLogs;
    }

    getHosLogsAsDictionary(getSystemLogs: boolean, overrideUser?: string) {
        const userId = this.getUserId(overrideUser);
        if (!userId) {
            return {};
        }

        let logs = extend(true, {}, (this.store.getState().userHosLogs[userId] || {})) as IIdDictionary<DutyStatusLog>;
        if (getSystemLogs) {
            let systemLogs = (this.store.getState().systemHosLogs[userId] || {});
            if (systemLogs) {
                logs = extend(true, {}, logs, systemLogs);
            }
        }
        return logs;
    }

    convertToList(logCollection: IIdDictionary<DutyStatusLog>): DutyStatusLog[] {
        let logList: DutyStatusLog[] = [];
        let logs = logCollection;

        if (typeof logs === "string") {
            logs = JSON.parse(logs);
        }
        if (logs) {
            Object.keys(logs).forEach(key => logList.push(new DutyStatusLog(logs[key])));
        }

        return logList;
    }

    // Hos Logs are returned in ascending order by default
    getHOSLogs(sort = true, overrideUser?: string, reverseOrder = false, getSystemLogs = false, getMalfunctionLogs = false): DutyStatusLog[] {
        let logs = this.getHosLogsAsDictionary(getSystemLogs, overrideUser);
        let logList: DutyStatusLog[] = this.convertToList(logs);

        if (getMalfunctionLogs) {
            logList = logList.filter(log => log.isMalfunctionLog());
        }
        if (sort) {
            return this.getSortedHosLogs(logList, reverseOrder);
        }

        return logList;
    }

    getSystemHOSLogs() {
        const userId = this.getUserId(undefined);
        if (!userId) {
            throw new Error("Invalid UserId");
        }

        let logs = extend(true, {}, this.store.getState().systemHosLogs[userId] || {}) as IIdDictionary<DutyStatusLog>;
        return this.convertToList(logs);
    }

    getTrackedHistoricalDays(dateKey?: string): IIdDictionary<IHistoricalDayEntry> {
        const userId = this.getUserId(undefined);
        if (!userId) {
            throw new Error("Invalid UserId");
        }

        const trackedDays = (dateKey
            ? { [dateKey]: this.store.getState().historicalDayTracker[userId]?.[dateKey] }
            : this.store.getState().historicalDayTracker[userId]) || {};
        const result: IIdDictionary<IHistoricalDayEntry> = {};

        Object.keys(trackedDays).forEach(key => {
            const entry = trackedDays[key];
            if (!entry) {
                return;
            }

            result[key] = {
                loadedAt: entry.loadedAt,
                hosLogs: entry.hosLogs.map(log => new DutyStatusLog(log)),
                systemLogs: entry.systemLogs.map(log => new DutyStatusLog(log)),
                violations: extend(true, [], entry.violations)
            };
        });

        return result;
    }

    hasUserLogs(overrideUser?: string): boolean {
        const userId = this.getUserId(overrideUser);
        if (!userId) {
            throw new Error("Invalid UserId");
        }

        let logs = this.store.getState().userHosLogs[userId] || [];
        return !!Object.keys(logs).length;
    }

    getUserLogsDictionary(overrideUser?: string): IIdDictionary<DutyStatusLog> {
        const userId = this.getUserId(overrideUser);
        if (!userId) {
            throw new Error("Invalid UserId");
        }

        let logs = this.store.getState().userHosLogs[userId];
        return this.convertToDictionary(logs);
    }

    getSystemLogsDictionary(overrideUser?: string): IIdDictionary<DutyStatusLog> {
        const userId = this.getUserId(overrideUser);
        if (!userId) {
            throw new Error("Invalid UserId");
        }

        let logs = this.store.getState().systemHosLogs[userId];
        return this.convertToDictionary(logs);
    }

    isChronologicalOrder(logCollection: DutyStatusLog[]): boolean {
        if (logCollection.length > 1) {
            let a = logCollection[0];
            let b = logCollection[logCollection.length - 1];
            return a.getDate().getTime() <= b.getDate().getTime();
        }

        return true;
    }

    getLatestLog(logCollection: DutyStatusLog[], beforeLog?: DutyStatusLog, logTypeFilter = (hosLog: DutyStatusLog) => hosLog.isNormalLogImpl()) {
        let toDate = beforeLog && beforeLog.getDate();
        let isChronological = this.isChronologicalOrder(logCollection);
        let start = isChronological ? logCollection.length - 1 : 0;
        let end = isChronological ? 0 : logCollection.length - 1;
        let delta = isChronological ? -1 : 1;
        let comparator = (a: number, b: number) => isChronological ? (a >= b) : (a <= b);

        if (!logCollection.length) {
            return null;
        }

        let j = end;
        for (let i = start; comparator(i, j); i = i + delta) {
            let log = logCollection[i];

            if (logTypeFilter(log) &&
                (!toDate || log.getDate().getTime() <= toDate.getTime()) &&
                (!beforeLog || beforeLog.id !== log.id)) {
                return log;
            }
        }

        return null;
    }

    getLatestStatusLog(userId?: string): DutyStatusLog | null {
        let logCollection = this.getSortedHosLogs(this.getHOSLogs(false, userId).filter(log => STANDARD_STATUSES.some(status => log.status === status)));
        return this.getLatestLog(logCollection);
    }

    getLogWithIdenticalDateTime(searchDate: Date) {
        let logCollection = this.getSortedHosLogs(this.getHOSLogs(false).filter(log => log.isNormalLog()));
        if (!logCollection.length) {
            return null;
        }

        for (let i = 0; i < logCollection.length; i++) {
            let log = logCollection[i];

            if (log.getDate() > searchDate) {
                return null;
            }

            if (searchDate.getTime() === log.getDate().getTime()) {
                return log;
            }
        }

        return null;
    }

// Given a sorted list of hos logs, finds the index of a given log efficiently
    binarySearch(logDate: string, sortedList: DutyStatusLog[]): number {
        let start = 0;
        let end = sortedList.length - 1;

        while (start <= end) {
            let mid = start + Math.floor((end - start) / 2);
            let midElement = sortedList[mid];
            if (logDate === midElement.dateTime) {
                return mid;
            } else if (logDate > midElement.dateTime) {
                start = mid + 1;
            } else {
                end = mid - 1;
            }
        }

        return -1;
    }

    getLatestEngineLog(hosLogs: DutyStatusLog[]) {
        return this.getLatestLog(hosLogs, undefined, (hosLog: DutyStatusLog) => hosLog.isEngineLog());
    }

    getLatestPcLog(hosLogs: DutyStatusLog[]) {
        return this.getLatestLog(hosLogs, undefined, (hosLog: DutyStatusLog) => hosLog.status === "PC" || hosLog.status === "PC_Exempted");
    }

    getCurrentDriverStatus(logCollection: DutyStatusLog[]) {
        let latestLog = this.getLatestLog(logCollection);
        return latestLog ? latestLog.status : "";
    }

    findWhere(logCollection: DutyStatusLog[], logProperty: keyof(DutyStatusLog), expectedProperty: string) {
        for (let i = 0; i < logCollection.length; i++) {
            if (logCollection[i][logProperty] === expectedProperty) {
                return logCollection[i];
            }
        }
        return null;
    }

    getArray(logDictionary: IIdDictionary<DutyStatusLog>) {
        let currentLogDictionary = logDictionary;
        if (typeof logDictionary === "string") {
            currentLogDictionary = logDictionary ? JSON.parse(logDictionary) : {};
        }

        let logs: any = [];
        for (const log in currentLogDictionary) {
            logs.push(currentLogDictionary[log]);
        }
        return logs;
    }

    getCurrentSituationDescription(status: string | undefined, logCollection: DutyStatusLog[]): string | null {
        let currentStatus = status || this.getCurrentDriverStatus(logCollection);

        switch (currentStatus) {
            case "PC_Exempted":
                return getCustomExemptHOSName(this.store.getState().systemSettings);
            case "PC":
            case "INT_PC":
                return "personal conveyance";
            case "YM":
                return "yard move";
            case "WT":
                return "Oil well wait time";
            case "WorkExemption":
                return "Work Exemption";
            case "RailroadExemption":
                return "Railroad Exemption";
            default:
                return null;
        }
    }

    getLogById(logCollection: DutyStatusLog[], id: string) {
        for (let log of logCollection) {
            if (log.id === id) {
                return log;
            }
        }
        return null;
    }

    getDuration(logCollection: DutyStatusLog[], log: DutyStatusLog, isReal = false) {
        let next = isReal ? this.getNormalNext(logCollection, log) : this.getNext(logCollection, log, true);
        let nextDate: Date = next ? next.getDate() : getServerDate();
        let duration = nextDate.getTime() - log.getDate().getTime();

        return duration < 0 ? (["Work", "WorkExemption"].indexOf(log.status) > -1 ? 15 * 1000 * 60 : 0) : duration;
    }

    getNeighborLog(logCollection: DutyStatusLog[], log: DutyStatusLog | null | undefined, isPrevious = false, skipControlLogs = false, normalOnly = false): DutyStatusLog | null {
        // A log can legitimately be missing (e.g. a placeholder whose parent log's day is not
        // loaded): there is no position to step from, so there is no neighbour either.
        if (!log) {
            return null;
        }
        let offset = this.isChronologicalOrder(logCollection) ? 1 : -1;
        let neighborIndex = this.indexOf(logCollection, log) + (isPrevious ? -1 : 1) * offset;
        let neighborLog = (neighborIndex >= 0 && neighborIndex < logCollection.length) ? logCollection[neighborIndex] : null;

        if (neighborLog && ((skipControlLogs && neighborLog.isControlLog()) || (normalOnly && !neighborLog.isNormalLog()))) {
            return this.getNeighborLog(logCollection, neighborLog, isPrevious, skipControlLogs, normalOnly);
        }

        return neighborLog;
    }

    indexOf(logCollection: DutyStatusLog[], log: DutyStatusLog | null | undefined) {
        if (!log) {
            return -1;
        }
        for (let i = 0; i < logCollection.length; i++) {
            if (logCollection[i].id === log.id) {
                return i;
            }
        }

        return -1;
    }

    isLogThatEndsExemptionPeriod(log: DutyStatusLog, exemptionStartLog: DutyStatusLog): boolean {
        return log.isStandardLog(true) && !log.requestedAdd && !(log.status === exemptionStartLog.status && log.device.id === exemptionStartLog.device.id);
    }

    getFirstExemptionLogInPeriod(exemptionLog: DutyStatusLog, exemptionLogIndex?: number, allLogs?: DutyStatusLog[]): DutyStatusLog {
        const logCollection = allLogs || this.getAllHOSStatusLogsIncludingSDC(true, undefined, false);
        const logIndex = exemptionLogIndex !== undefined ? exemptionLogIndex : this.indexOf(logCollection, exemptionLog);

        if (logIndex === -1) {
            return exemptionLog;
        }

        let firstExemptionLog = exemptionLog;
        for (let i = logIndex - 1; i >= 0; i--) {
            const log = logCollection[i];
            if (log.status === exemptionLog.status && log.device.id === exemptionLog.device.id) {
                firstExemptionLog = log;
                continue;
            }

            // If we get another standard log that is not the same status/device as the current exemption log, end search as current exemption period is over
            if (log.isSituationalDrivingClear() || this.isLogThatEndsExemptionPeriod(log, exemptionLog)) {
                break;
            }
        }
        return firstExemptionLog;
    }

    getLastLogInExemptionPeriod(exemptionLog: DutyStatusLog, exemptionLogIndex?: number, allLogs?: DutyStatusLog[]): DutyStatusLog | null {
        const logCollection = allLogs || this.getAllHOSStatusLogsIncludingSDC(true, undefined, false);
        const logIndex = exemptionLogIndex !== undefined ? exemptionLogIndex : this.indexOf(logCollection, exemptionLog);

        if (logIndex === -1) {
            return null;
        }

        for (let i = logIndex + 1; i < logCollection.length; i++) {
            const log = logCollection[i];
            if (log.isSituationalDrivingClear()) {
                return log;
            }
            if (this.isLogThatEndsExemptionPeriod(log, exemptionLog)) {
                break;
            }
        }
        return null;
    }

    getRelatedRequestedLogIfExists(exemptionLog: DutyStatusLog, exemptionLogIndex?: number, allLogs?: DutyStatusLog[]): DutyStatusLog | null {
        // If removal, method looks for requestedAdd immediately after itself
        // If shortening, method looks for SDC after itself
        const isRemoval = exemptionLog.requestedEdit?.isRequestedRemoval;

        const logCollection = allLogs || this.getAllHOSStatusLogsIncludingSDC(true, undefined, false);
        const logIndex = exemptionLogIndex !== undefined ? exemptionLogIndex : this.indexOf(logCollection, exemptionLog);
        if (logIndex === -1) {
            return null;
        }

        for (let i = logIndex + 1; i < logCollection.length; i++) {
            const log = logCollection[i];
            if (isRemoval && log.isStandardLog() && log.requestedAdd) {
                return log;
            }

            if (log.isSituationalDrivingClear()) {
                return !isRemoval && log.requestedEdit ? log : null;
            }

            // If we get another standard log that is not the same status/device as the current exemption log, end search as current exemption period is over
            if (this.isLogThatEndsExemptionPeriod(log, exemptionLog)) {
                break;
            }
        }
        return null;
    }

    getNormalNext(logCollection: DutyStatusLog[], log: DutyStatusLog | null | undefined) {
        return this.getNeighborLog(logCollection, log, false, true, true);
    }

    getNext(logCollection: DutyStatusLog[], log: DutyStatusLog | null | undefined, skipControlLogs = true) {
        return this.getNeighborLog(logCollection, log, false, skipControlLogs);
    }

    getPrevious(logCollection: DutyStatusLog[], log: DutyStatusLog | null | undefined, skipControlLogs = true) {
        return this.getNeighborLog(logCollection, log, true, skipControlLogs);
    }

    findModifiedExemptionLogsAndFilterRequestedAdds(logs: DutyStatusLog[]): [Set<string>, DutyStatusLog[]] {
        // `Logs` is presumed to NOT include SDC logs, they are not included in the final output.
        // This method is for UI display purposes
        const requestedAddShortenYMPCLogIdSet = new Set<string>();

        const filteredLogs: DutyStatusLog[] = [];
        let lastPcYmLog: DutyStatusLog | null = null;
        let currentRangeRequestedAdds: DutyStatusLog[] = [];
        const isPendingAddRequestLog = (log: DutyStatusLog, rangeStartLog: DutyStatusLog) => {
            return log.requestedAdd && HOS_STANDARD_STATUSES.indexOf(log.status) > -1
                && log.getDeviceId() === rangeStartLog.getDeviceId()
                && !this.isReviewed(log);
        };

        const sdcLogs = this.getSystemHOSLogs().filter(log => log.isSituationalDrivingClear() && !log.isRemoved());
        const logCollection = this.getSortedHosLogs(logs.concat(sdcLogs));

        for (const log of logCollection) {
            if (log.isEditableExemptionLog()) {
                if (!lastPcYmLog) {
                    filteredLogs.push(log);
                    lastPcYmLog = log;
                    continue;
                }

                if (log.status === lastPcYmLog?.status && log.getDeviceId() === lastPcYmLog?.getDeviceId()) {
                    filteredLogs.push(log);
                    continue;
                }
            }

            const isSituationalDrivingClear = log.isSituationalDrivingClear();
            if (lastPcYmLog) {
                if (isPendingAddRequestLog(log, lastPcYmLog)) {
                    currentRangeRequestedAdds.push(log);
                    continue;
                }

                if (isSituationalDrivingClear || log.isStandardLog(true)) {
                    let logWithEdit = lastPcYmLog.requestedEdit?.isRequestedRemoval ? lastPcYmLog : (isSituationalDrivingClear && log.requestedEdit ? log : null);
                    if (!logWithEdit || !logWithEdit.requestedEdit || this.isReviewed(logWithEdit.requestedEdit)) {
                        // PC/YM is not in a valid edit state, do not hide the requested adds
                        filteredLogs.push(...currentRangeRequestedAdds);
                    } else {
                        requestedAddShortenYMPCLogIdSet.add(lastPcYmLog.id);
                    }
                    currentRangeRequestedAdds = [];
                    lastPcYmLog = log.isEditableExemptionLog() ? log : null;
                }
            }
            if (!isSituationalDrivingClear) {
                // Don't add SDC to final list
                filteredLogs.push(log);
                continue;
            }
        }
        if (currentRangeRequestedAdds.length) {
            filteredLogs.push(...currentRangeRequestedAdds);
        }

        return [requestedAddShortenYMPCLogIdSet, this.getSortedHosLogs(filteredLogs)];
    }

    getUnverifiedLocalDays(logCollection: DutyStatusLog[], dateRange: IDateRange, requestedAddShortenYMPCLogIdSet?: Set<string>): { [day: string]: boolean } {
        let unverifiedDays: { [day: string]: boolean } = {};

        for (let i = 0; i < logCollection.length; i++) {
            let log = logCollection[i];
            let logDate = log.getDate();

            if (logDate < dateRange.startDate || logDate >= dateRange.endDate) {
                continue;
            }
            let key = getDateTokenString(log.getDate(), true);

            if (!log.isVerifiedForDisplay(true) || (requestedAddShortenYMPCLogIdSet && requestedAddShortenYMPCLogIdSet.has(log.id))) {
                unverifiedDays[key] = true;
            }
        }

        return unverifiedDays;
    }

    /**
     * Searches a collection to find the parent log of the placeholder and checks if an edit to the parent log was made after the placeholder was certified
     * @param logs The collection of logs to search
     * @param placeholderDate The date of the placeholder to check
     * @param certificationLogCreationDate The date of the certification log created for the placeholder
     */
    isEditedAfterVerification(logs: DutyStatusLog[], placeholderDate: Date, certificationLogCreationDate: Date): boolean {
        for (let log of logs) {
            let logDate = new Date(log.dateTime);
            if (!log.isPlaceholder() && STANDARD_STATUSES.indexOf(log.status) > -1 && logDate < placeholderDate && log.editDateTime) {
                let editDate = new Date(log.editDateTime);
                if (editDate > certificationLogCreationDate) {
                    return true;
                }
                return false;
            }
        }

        return false;
    }

    getAnnotationAdditionForDeferralExemption(log: DutyStatusLog) {
        if (log.status !== "ExemptionOffDutyDeferral" || !log.deferralStatus) {
            return "";
        }
        const additions: IIdDictionary<string> = {
            "DayOne": "Day one",
            "DayTwo": "Day two"
        };
        if (this.featureFlagsService.hasFeatureFlag(FeatureFlagName.ENABLE_AUTO_CREATE_NONE_DEFERRAL_LOG)) {
            additions["None"] = "Deferral Off";
        }
        return additions[log.deferralStatus] ?? "";
    }

    getAnnotations(log: DutyStatusLog, translator: Translator) {
        // A copy, so the display-only labels added below never land on the log itself
        let annotations: (IAnnotation | INewAnnotation)[] = [...(log.annotations || [])];
        let annotationByStatus: IIdDictionary<string> = {
            "Exemption16H": "16-hour exemption",
            "AdverseWeather": "Adverse driving conditions",
            "Connected": "Power connected",
            "Disconnected": "Power disconnected",
            "WT": "Oil Well Wait Time exemption",
            "Login": "Assigned to {0}",
            "Logoff": "Unassigned from {0}",
            "ExemptionOffDutyDeferral": "Off-duty deferral exemption",
            "RailroadExemption": "Railroad exemption",
            "ExemptionReducedRest": "Alberta reduced rest provision",
            "EmergencyResponseExemptionEnabled": "Emergency response exemption started",
            "EmergencyResponseExemptionDisabled": "Emergency response exemption ended"
        };
        let annotation: string = annotationByStatus[log.status];
        let addition: string = this.getAnnotationAdditionForDeferralExemption(log);

        if (annotation) {
            return translator.translate([annotation, addition, "No vehicle", "{0} minutes deferred"])
                .then(([translatedAnn, add, localizedNoVehicle, durationTmpl]) => {
                    if (log.isLoginLogoffLog()) {
                        if (log.device.id === "NoDeviceId") {
                            annotations.push({"comment": translatedAnn.replace("{0}", localizedNoVehicle)});
                        } else if (log.device) {
                            let device = this.deviceCache.get({id: log.device.id});
                            let deviceName = device.length ? device[0].name : localizedNoVehicle;
                            annotations.push({"comment": translatedAnn.replace("{0}", deviceName)});
                        }
                    } else {
                        let ann = translatedAnn;
                        if (add) {
                            ann += ` - ${ add }`;
                        }

                        if (log.deferralMinutes) {
                            ann += ` - ${ durationTmpl.replace("{0}", log.deferralMinutes.toString()) }`;
                        }

                        annotations.push({"comment": ann});
                    }

                    return annotations;
                });
        }

        return Promise.resolve(annotations);
    }

    isLastLogOfDay(logCollection: DutyStatusLog[], log: DutyStatusLog): boolean {
        let next = this.getNext(logCollection, log, false);
        if (next) {
            let logDate = dstCalculator.toLocalDateTime(log.getDate());
            let nextDate = dstCalculator.toLocalDateTime(next.getDate());

            return !((logDate.getFullYear() === nextDate.getFullYear()) && (logDate.getMonth() === nextDate.getMonth()) && (logDate.getDate() === nextDate.getDate()));
        }

        return true;
    }

    isRequestedLogsDay(currentUser: IUser | DeepReadonly<IUser> | null, logCollection: DutyStatusLog[], dateTime?: Date): boolean {
        let dailyLogs = logCollection;
        if (dateTime) {
            let reportPeriod: IReportPeriod = getLocalDaySpan(dateTime);
            dailyLogs = this.getLogsByDate(logCollection, reportPeriod, false, false);
        }
        return dailyLogs.some(log => log.hasPendingRequestForCurrentUser(currentUser));
    }

    isUnverifiedLogsDay(logCollection: DutyStatusLog[]): boolean {
        return logCollection.some(log => (!log.isVerifiedForDisplay(true)));
    }

    isMissingAddressDay(logCollection: DutyStatusLog[], dateTime?: Date): boolean {
        let dailyLogs = logCollection;
        if (dateTime) {
            let reportPeriod: IReportPeriod = getLocalDaySpan(dateTime);
            dailyLogs = this.getLogsByDate(logCollection, reportPeriod, false, true);
        } else {
            dailyLogs = dailyLogs.filter(log => log.isNormalLog());
        }
        return dailyLogs.some(log => log.isMissingAddress());
    }

    isPlaceholderRequestedEdit(logCollection: DutyStatusLog[], log: DutyStatusLog): boolean | null {
        let parentLogId = log["parentId"];
        let originalLog: DutyStatusLog | null = null;

        if (log.isPlaceholder() && parentLogId) {
            originalLog = this.findWhere(logCollection, "id", parentLogId);
        }

        return originalLog && originalLog.hasPendingRequest();
    }

    getLogsByDate(logCollection: DutyStatusLog[], reportPeriod: IReportPeriod, includeOverlapped = false, isReal = false) {
        let getTimeStamp = (date: Date | string) => toDateTime(date).getTime();
        let fromTimestamp = getTimeStamp(reportPeriod.fromDate);
        let toTimestamp = getTimeStamp(reportPeriod.toDate);
        let isPeriodLog = (logTimestamp: number) => logTimestamp >= fromTimestamp && logTimestamp < toTimestamp;
        let isOverlappedLog = (logTimestamp: number, nextLogTimestamp?: number) => logTimestamp < fromTimestamp && (!nextLogTimestamp || nextLogTimestamp > fromTimestamp);
        let collection = isReal ? logCollection.filter((l, _) => l.isNormalLogImpl(true)) : logCollection;

        return collection.filter((log, index: number) => {
            let logDate = getTimeStamp(log.getDate());
            let nextLog: DutyStatusLog | undefined = collection[index + 1];
            let nextLogDate = nextLog && getTimeStamp(nextLog.getDate());
            return (includeOverlapped && isOverlappedLog(logDate, nextLogDate)) || isPeriodLog(logDate);
        });
    }

    hasRequestedEdits(logCollection: DutyStatusLog[]): boolean {
        return logCollection.some(log => {
            return log.state === REQUESTED || log.state === "Transitioning" || (!!log.requestedEdit && (log.requestedEdit.state === REQUESTED || log.requestedEdit.state === "Transitioning"));
        });
    }

    getRequestedEdits(logCollection: DutyStatusLog[]): any {
        return logCollection.filter(log => {
            return log.state === REQUESTED || log.state === "Transitioning" || (!!log.requestedEdit && (log.requestedEdit.state === REQUESTED || log.requestedEdit.state === "Transitioning"));
        });
    }

    getNotVerifiedLogs(logCollection: DutyStatusLog[]) {
        return logCollection.filter(log => (!log.isVerifiedForDisplay(true)));
    }

    getSortedHosLogs(logCollection: DutyStatusLog[], reverseOrder = false): DutyStatusLog[] {
        return logCollection.sort((a, b) => reverseOrder ? compareDatesWithSequenceIds(b, a) : compareDatesWithSequenceIds(a, b));
    }

    getDailyStatusInfo(date: Date, vehicleId: string, store: DriveStore) {
        const userId = this.getUserId(undefined);
        if (!userId) {
            return null;
        }
        let dailyOdometer = store.getState().dailyOdometer[userId];
        let dateString = getDateTokenString(date);

        return dailyOdometer[dateString] && dailyOdometer[dateString][vehicleId] ? dailyOdometer[dateString][vehicleId] : null;
    }

    getAllHOSStatusLogsIncludingSDC(includeRequestedAdds = true, controlStatuses: THosStatus[] = SITUATIONAL_DRIVING_CLEAR_STATUS, includeIntermediateLogs = true): DutyStatusLog[] {
        return this.getSortedHosLogs(this.getHOSLogs(false, undefined, false, true).filter(log => log.isNormalLog(false, false, controlStatuses, includeRequestedAdds, includeIntermediateLogs)));
    }

    getNormalPrevious(logCollection: DutyStatusLog[], log: DutyStatusLog | null | undefined): any | null {
        return this.getNeighborLog(logCollection, log, true, true, true);
    }

    // Allow a log to be added at most 15min in the future
    isInvalidFutureDate(newDate: Date, previousLog?: DutyStatusLog | null) {
        let serverDate = getServerDate();
        if (newDate > serverDate) {
            if (!previousLog || previousLog.status !== "Work" || newDate > roundBasedOnStatus(serverDate, previousLog.status)) {
                return true;
            }
        }
        return false;
    }

    validateDateTime(
        newValueDateString: string,
        errorLogger: ValidationErrorLogger,
        oldValueDateString?: string,
        previousEwdLog?: DutyStatusLog | null
    ): boolean {
        let newValueDate: Date | null = newValueDateString ? new Date(newValueDateString) : null;
        let oldValueDate: Date | null = oldValueDateString ? new Date(oldValueDateString) : null;

        if (newValueDate && oldValueDate && newValueDate.getTime() === oldValueDate.getTime()) {
            return true;
        }
        if (!newValueDate) {
            errorLogger.addError("Set a date and time for this log.");
            return false;
        }
        if (this.isInvalidFutureDate(newValueDate, previousEwdLog)) {
            errorLogger.addError("You've selected date in future, please select correct one");
            return false;
        }

        return true;
    }

    validatePreviousLogStatus(
        newValue: DutyStatusLog,
        errorLogger: ValidationErrorLogger,
        collection: DutyStatusLog[],
        oldValue: DutyStatusLog | null
    ): boolean {
        let newValueDate = new Date(newValue.dateTime);
        let oldValueDate: Date | null = oldValue ? new Date(oldValue.dateTime) : null;

        // Skip validation if log already exists at same time
        if (newValueDate && oldValueDate && newValueDate.getTime() === oldValueDate.getTime()) {
            return true;
        }

        let newValuePrevLog = collection && this.getLatestLog(collection, newValue);
        let oldValuePrevLog = collection && this.getNormalPrevious(collection, newValue);
        if ((newValuePrevLog && newValuePrevLog.isAutomaticDriving()) || (oldValuePrevLog && oldValuePrevLog.isAutomaticDriving())) {
            errorLogger.addError("This change is not allowed because it will affect the date or duration of driving time.");
            return false;
        }

        let logCollectionWithControl = this.getAllHOSStatusLogsIncludingSDC(true, undefined, false);
        newValuePrevLog = getPreviousOrNextByDate(logCollectionWithControl, newValueDate, true);
        let newValueNextLog = getPreviousOrNextByDate(logCollectionWithControl, newValueDate, false);
        if (newValuePrevLog && newValueNextLog
            && (isSituationalDrivingStatus(newValuePrevLog.status)
                || newValuePrevLog.isSituationalDrivingClear())) {
            errorLogger.addError("You cannot change an existing situational driving status.");
            return false;
        }

        if (oldValueDate) {
            oldValuePrevLog = getPreviousOrNextByDate(logCollectionWithControl, oldValueDate, true);
            if (oldValuePrevLog
                && (isSituationalDrivingStatus(oldValuePrevLog.status)
                    || oldValuePrevLog.isSituationalDrivingClear())) {
                errorLogger.addError("You cannot change an existing situational driving status.");
                return false;
            }
        }

        // Block datetime changes that would fall within a previous DrivingWhileInExemption period
        if (isDateTimeInPreviousDwieRange(newValue.dateTime, this)) {
            errorLogger.addError("This change is not allowed because it will affect the date or duration of driving time.");
            return false;
        }
        return true;
    }

    validateDevice(log: DutyStatusLog, value: IIdEntity, errorLogger: ValidationErrorLogger): boolean {
        let status = log.status;
        let allowedStatuses = ["ON", "OFF", "Rest", "Work", "WorkExemption"];

        if ((!value || !value.id || value.id === "NoDeviceId") && allowedStatuses.indexOf(status) === -1) {
            errorLogger.addError("An HOS log without a vehicle can only be in ON or OFF status.");
            return false;
        }
        return true;
    }

    validateAnnotations(value = [] as any, errorLogger: ValidationErrorLogger, oldValue: any): boolean {
        let changedAnnotations = value.filter((annotation: IAnnotation) => {
            return !oldValue || !oldValue.some((prevAnnotation: IAnnotation) => prevAnnotation.id === annotation.id);
        });
        let annotationText = changedAnnotations.length && changedAnnotations[0].comment.trim();

        if (((!oldValue || !oldValue.length) && !annotationText) || !annotationText) {
            errorLogger.addError("Add an annotation to this log.");
            return false;
        } else if (annotationText.length < 4) {
            errorLogger.addError("Annotation must be at least 4 characters long.");
            return false;
        }
        if (annotationText.length > 60) {
            errorLogger.addError("Annotation cannot be longer than 60 characters.");
            return false;
        }
        return true;
    }

    areCollectionsEqual(logCollection: DutyStatusLog[], newCollection: DutyStatusLog[], ignoredKeys = ["_duration", "_createdDate", "editDateTime"]) {
        return isEqual(logCollection, newCollection, ignoredKeys);
    }

    checkBoundaries(index: number, list: DutyStatusLog[]) {
        return (index >= 0 && index < list.length);
    }

    isAllowedToBePaired = (log: DutyStatusLog) => ALLOWED_STATUS_TO_BE_PAIRED_WITH_AUTO_D.indexOf(log.status) > -1 || log.isManualDriving();

    findPairedLog(hosLog: DutyStatusLog, allSortedHosLogs: DutyStatusLog[]): DutyStatusLog | null {
        if (!hosLog.isAutomaticDriving() && !this.isAllowedToBePaired(hosLog)) {
            return null;
        }
        let direction = hosLog.isAutomaticDriving() ? 1 : -1;
        let index = this.binarySearch(hosLog.dateTime, allSortedHosLogs);
        while (this.checkBoundaries(index + direction, allSortedHosLogs) && allSortedHosLogs[index + direction].placeholder) {
            index += direction;
        }
        let boundaryLogIndex = index + direction;
        if (this.checkBoundaries(boundaryLogIndex, allSortedHosLogs)) {
            let boundaryLog = allSortedHosLogs[boundaryLogIndex];
            if ((direction === -1 && boundaryLog.isAutomaticDriving()) || this.isAllowedToBePaired(boundaryLog)) {
                if (boundaryLog.device.id !== hosLog.device.id) {
                    return null;
                }
                return boundaryLog;
            }
        }
        return null;
    }

    saveRequest(log: DutyStatusLog, isAccepted = true) {
        if (log.isAwaitingTransition()) {
            return;
        }

        if (!log.requestedEdit) {
            log.state = isAccepted ? "Active" : "Rejected";
        } else {
            const requestedChanges = !log.requestedEdit ||
                (log.requestedEdit.state !== "Requested" && log.requestedEdit.state !== "Transitioning") ?
                null : log.getRequestChanges();

            if (isAccepted && requestedChanges) {
                const isSituationalDrivingClear = log.status === SITUATIONAL_DRIVING_CLEAR;
                for (const prop in requestedChanges) {
                    if (requestedChanges.hasOwnProperty(prop)) {
                        if (prop === "driver" && log.driver.id !== requestedChanges[prop].id) {
                            if (log.requestedEdit.state === "Transitioning" ||
                                (log.requestedEdit.state === "Requested" &&
                                (log.driver.id === "NoUserId" || log.requestedEdit.driver.id === "NoUserId"))) {
                                log.originalDriverBeforeEdit = log.driver.id;
                                log[prop] = requestedChanges[prop];
                            } else {
                                log._awaitingTransition = true;
                            }
                        } else {
                            if (prop === "dateTime" && isSituationalDrivingClear) {
                                log._originalDateTimeBeforeEdit = log.dateTime;
                            }
                            (log as any)[prop] = requestedChanges[prop];
                        }
                    }
                }
            }
            log.requestedEdit.state = isAccepted ? "Active" : "Rejected";
        }
    }

    clone(logCollection: DutyStatusLog[]) {
        let clonedLogs: DutyStatusLog[] = [];
        logCollection.forEach(log => {
            clonedLogs.push(new DutyStatusLog(log));
        });
        return clonedLogs;
    }

    createManualDutyStatusLog(
        dateTime: string,
        currentVehicle: IIdEntity | null,
        currentDriver: DeepReadonly<IUser>,
        coDrivers: IIdEntity[],
        logStatus: THosStatus,
        origin: THosOrigin = "Manual",
        annotation = "",
        location?: IAddressLookupData
    ) {
        let newLog = new DutyStatusLog({
            id: idSerializer.get(),
            dateTime,
            status: logStatus,
            device: { id: currentVehicle ? currentVehicle.id : "NoDeviceId" },
            driver: { id: currentDriver.id },
            coDrivers: coDrivers,
            origin
        });

        if (location) {
            newLog.location = location;
        }

        if (annotation !== "") {
            newLog.annotations = [{
                id: idSerializer.get(),
                driver: { id: currentDriver.id },
                comment: annotation,
                dateTime
            }];
        }
        return newLog;
    }

    reachedDailyPCDrivingLimit(apiUserId: string | undefined): {
        reachedLimit: boolean;
        latestPCLog?: DutyStatusLog | undefined;
    } {
        let allLogsForPC = this.getHOSLogs(false, apiUserId, undefined, true);
        const logType = allLogsForPC.some(log => log.status === "MaxPCLimitReached")
            ? "MaxPCLimitReached"
            : "SituationalDrivingClear";
        const requiredLogsLength = logType === "SituationalDrivingClear" ? 3 : 2;

        const statusesForPC = ENGINE_STATUSES.concat(STANDARD_STATUSES).concat([logType]);
        allLogsForPC = this.getSortedHosLogs(allLogsForPC.filter(hosLog => hosLog.isStandardStatusDriverLog() && statusesForPC.indexOf(hosLog.status) !== -1));

        if (allLogsForPC.length < 1) {
            return { reachedLimit: false, latestPCLog: undefined };
        }

        const standardLogs = allLogsForPC.filter(log => !log.isControlLog([logType]));
        const standardLogsLength = standardLogs.length;

        if (standardLogsLength < requiredLogsLength) {
            return { reachedLimit: false, latestPCLog: undefined };
        }

        const latestPCLog = standardLogs[standardLogsLength - requiredLogsLength];
        if (latestPCLog.status !== "PC") {
            return { reachedLimit: false, latestPCLog: undefined };
        }

        const latestLog = standardLogs[standardLogsLength - 1];
        const isPowerCycleExitCondition = (logs: DutyStatusLog[], pc: DutyStatusLog, d: DutyStatusLog) =>
            logs.some(l => l.getDate() > pc.getDate() && l.getDate() < d.getDate() && l.status === "EngineShutdownPC");

        const reachedLimit = logType === "SituationalDrivingClear"
            ? standardLogs[standardLogsLength - 2].status === logType && standardLogs[standardLogsLength - 2].isAutomaticLog()
            && latestLog.status === "D" && latestLog.isAutomaticLog()
            && !isPowerCycleExitCondition(allLogsForPC, latestPCLog, latestLog)
            : latestLog.status === logType && latestLog.isAutomaticLog()
            && !isPowerCycleExitCondition(allLogsForPC, latestPCLog, latestLog);

        return { reachedLimit, latestPCLog };
    }

    isPCLimitExceeded
    (
        ruleSetKey: string,
        user: DeepReadonly<IUser> | null,
        cachedLogs: DutyStatusLog[],
        maxPCDistance: number,
        hosProcess: HosProcess,
        currentOdometer?: number,
        sync?: Sync,
        model?: HosMainModel
    ): IPCLimitDetails {
        let pcLimitDetails: IPCLimitDetails = {
            isPCLimitExceeded: false,
            latestPCLog: null
        };

        if (cachedLogs.length <= 0) {
            return pcLimitDetails;
        }

        let currentDate = getServerDate();
        let fromDate = getStartOfDay(dstCalculator.toLocalDateTime(currentDate));
        let userPCLimitInMeters = user?.maxPCDistancePerDay !== undefined && user?.maxPCDistancePerDay >= 0
            ? user.maxPCDistancePerDay
            : maxPCDistance;

        let startOfDay = this.getCurrentRuleSetStartOfDay(hosProcess);
        if (CANADARULESETS.some((ruleSet) => ruleSet.indexOf(ruleSetKey) > -1)) {
            if (startOfDay !== "") {
                let dateParts = startOfDay.split(":");
                let hours = parseInt(dateParts[0]);
                let min = parseInt(dateParts[1]);
                let sec = parseInt(dateParts[2]);

                fromDate.setHours(hours);
                fromDate.setMinutes(min);
                fromDate.setSeconds(sec);
            }
            if (userPCLimitInMeters > constants.CANADA_MAX_PC_LIMIT || userPCLimitInMeters <= 0) {
                userPCLimitInMeters = constants.CANADA_MAX_PC_LIMIT;
            }
        }
        let fromDateUtc = dstCalculator.toReverseOffsetDateTime(fromDate, user?.timeZoneId);

        // If the start of date is set by the user, the day may not have started yet. So we would need to use the previous day.
        if (fromDateUtc.getTime() > currentDate.getTime()) {
            fromDate.setDate(fromDate.getDate() - 1);
            fromDateUtc = dstCalculator.toReverseOffsetDateTime(fromDate, user?.timeZoneId);
        }
        let logsInADay = cachedLogs
        .filter(log => new Date(Date.parse(log.dateTime)) >= fromDateUtc && log.driver.id !== NO_USER_ID && isStatusLog(log))
        .sort((a, b) => a.dateTime < b.dateTime ? -1 : 1);

        let totalPCDistance = this.getTotalPCDistance(logsInADay);
        let lastPCLog = this.getLatestLog(logsInADay, undefined, (hosLog: DutyStatusLog) => this.isPCLog(hosLog));

        if (currentOdometer && lastPCLog) {
            if (!lastPCLog.odometer || lastPCLog.odometer === 0 || lastPCLog.odometer > currentOdometer) {
                lastPCLog.odometer = currentOdometer;
                if (sync) {
                    void sync.dutyStatusLogs.setItem(lastPCLog);
                }
                return {
                    isPCLimitExceeded: false,
                    latestPCLog: lastPCLog
                };
            } else {
                totalPCDistance += currentOdometer - lastPCLog.odometer;
            }
        }

        if (model) {
            if (user?.isMetric && userPCLimitInMeters > 0) {
                model.maxPCDistance = (userPCLimitInMeters / 1000).toFixed(1) + " km";
            } else if (!user?.isMetric && userPCLimitInMeters > 0) {
                model.maxPCDistance = (userPCLimitInMeters / 1609).toFixed(1) + " mi";
            }
        }

        let logObject = {
            logs: logsInADay.map(log => ({
                logId: log.id,
                dateTime: log.dateTime,
                status: log.status,
                odometer: log.odometer,
                isPlaceHolder: log.isPlaceholder()
            })),
            maxPCDistance: maxPCDistance,
            totalPCDistance: totalPCDistance,
            userPCLimitInMeters: userPCLimitInMeters,
            currentOdometer: currentOdometer,
            fromDate: fromDate,
            timeZoneId: user?.timeZoneId,
            currentDate: currentDate,
            fromDateUtc: fromDateUtc,
            startOfDay: startOfDay
        };
        let isPCLimitExceeded = totalPCDistance > (userPCLimitInMeters <= 0 ? Number.MAX_VALUE : userPCLimitInMeters);

        if (isPCLimitExceeded && lastPCLog && !this.store.getState().mobile.pcLimitWarningMessage) {
            logger.event(
                logger.Event.PCEXCEEDCONDITION,
                `checkPCLimitExceeded: ${ JSON.stringify(logObject) } | isPCWarningDismissed: ${ isPCLimitExceeded }`,
                user
            );
        }


        return {
            isPCLimitExceeded: isPCLimitExceeded,
            latestPCLog: lastPCLog
        };
    }

    getTotalPCDistance(logsInADay: DutyStatusLog[]) {
        // Distance traveled in PC_Exempted should not be included
        let totalPCDistance = 0.0;
        for (let i = 1; i < logsInADay.length; i++) {
            if (this.isPCLog(logsInADay[i - 1])) {
                if (logsInADay[i].hasOwnProperty("odometer") && logsInADay[i - 1].hasOwnProperty("odometer")) {
                    let prevOdometer = logsInADay[i - 1].odometer ?? 0;
                    let currOdometer = logsInADay[i].odometer ?? 0;
                    if (currOdometer > prevOdometer) {
                        totalPCDistance += currOdometer - prevOdometer;
                    }
                }
            }
        }
        return totalPCDistance;
    }

    isPCLog(log: DutyStatusLog): boolean {
        return log.status === "PC" || log.status === "INT_PC";
    }

    getCurrentRuleSetStartOfDay(hosProcess: HosProcess): string {
        let nearest = hosProcess.getLatestHosRuleSet();
        return nearest && nearest.startOfDay ? nearest.startOfDay : "";
    }

    getRuleSetStartOfDayForDate(hosProcess: HosProcess, date: Date): string {
        let nearest = hosProcess.getUserHosRuleSets().getLatestRuleSetBeforeDate(date);

        if (nearest && nearest.startOfDay) {
            return nearest.startOfDay;
        } else if (nearest && nearest.hosRuleSet) {
            return "00:00:00";
        } else {
            return "";
        }
    }

    getRuleSetForDate(hosProcess: HosProcess, date: Date): string {
        let nearest = hosProcess.getUserHosRuleSets().getLatestRuleSetBeforeDate(date);

        if (nearest && nearest.hosRuleSet) {
            return nearest.hosRuleSet.id;
        } else {
            return "";
        }
    }

    removeHOSLogs(logToRemoveIds: string[], overrideUser?: string) {
        const userId = this.getUserId(overrideUser);

        if (logToRemoveIds.length <= 0 || !userId) {
            return;
        }

        let logs = this.getHosLogsAsDictionary(false, userId);
        let logsToRemain: IIdDictionary<DutyStatusLog> = {};

        for (const logKey in logs) {
            let parentId = logs[logKey].parentId;
            if (parentId && logToRemoveIds.indexOf(parentId) < 0) {
                logsToRemain[logKey] = logs[logKey];
            }
        }

        this.store.dispatch(HosLogActions.setUserHosLogs({ userId: userId.toString(), logs: logsToRemain }));
    }

    getLogBasedOnDate(searchDate: Date, beforeLog: boolean, editedLogId?: string): DutyStatusLog | null {
        let foundLog: DutyStatusLog | null = null;
        let logCollection = this.getSortedHosLogs(this.getHOSLogs().filter(log => log.isNormalLog()), !beforeLog);
        let breakCondition = beforeLog ? (log: DutyStatusLog) => log.getDate() > searchDate : (log: DutyStatusLog) => log.getDate() < searchDate;

        for (let i = 0; i < logCollection.length; i++) {
            let log = logCollection[i];
            if (breakCondition(log)) {
                break;
            }
            if (!editedLogId || editedLogId !== log.id) {
                foundLog = log;
            }
        }

        return foundLog;
    }

    getLogsToUnverify(logToUnverify: DutyStatusLog): DutyStatusLog[] {
        let logsToEdit: DutyStatusLog[] = [];
        let placeHolders = this.getHOSLogs()
            .filter(hosLog => !!hosLog.placeholder && hosLog.parentId === logToUnverify.id);

        logToUnverify.unverify();
        logsToEdit.push(logToUnverify);

        placeHolders.forEach(p => {
            p.unverify();
            logsToEdit.push(p);
        });

        return logsToEdit;
    }

    getAllPairedLogs(sortedHOSLogs: DutyStatusLog[]): DutyStatusLog[] {
        if (sortedHOSLogs.length === 0) {
            return [];
        }
        let pairedLogSet = new Set<string>();

        for (let currentLog of sortedHOSLogs) {
            if (currentLog.isHidden || pairedLogSet.has(currentLog.id)) {
                continue;
            }

            let pairedLog = this.findPairedLog(currentLog, sortedHOSLogs);

            if (pairedLog && !currentLog.isHidden && !pairedLog.isHidden) {
                pairedLogSet.add(currentLog.id);
                pairedLogSet.add(pairedLog.id);
            }
        }

        const logsWithPairedLogs = sortedHOSLogs.filter(currentLog => {
            return pairedLogSet.has(currentLog.id);
        });

        return logsWithPairedLogs.reverse();
    }

    getLastLogIfDrivingWhileInExemption(userId: string, exemptionLog: DutyStatusLog, endDate: string): DutyStatusLog | null {
        // Find the first exemption log in the period to ensure we check the entire range
        const firstExemptionLog = this.getFirstExemptionLogInPeriod(exemptionLog);

        const logs = this.getSortedHosLogs(
            this.getHOSLogs(false, userId, false, true)
                .filter(log => drivingWhileInExemptionStatuses.indexOf(log.status) > -1 && log.device.id === exemptionLog.device.id && log.dateTime < endDate),
            true
        );
        const lastLog = logs.length ? logs[0] : null;
        return lastLog && lastLog.status === "DrivingWhileInExemption" && lastLog.dateTime >= firstExemptionLog.dateTime && lastLog.device.id === exemptionLog.device.id
            ? lastLog
            : null;
    }

    getLogsToEndDrivingExemption(
        user: IUser | undefined | null,
        exemptionLog: DutyStatusLog | null,
        endTime: Date,
        origin: THosOrigin = "Manual",
        status?: THosStatus,
        includeStandardLog = true,
        skipSdc?: boolean
    ): DutyStatusLog[] {
        if (skipSdc || !user?.id || !exemptionLog || !exemptionLog.isSituationalDrivingStatus()) {
            return [];
        }

        const coDrivers = this.userService.getCoDrivers(user.name);
        const device = exemptionLog.device;
        const newLogs: DutyStatusLog[] = [
            this.createManualDutyStatusLog(new Date(endTime.getTime() - SDC_DIFFERENCE_FROM_LOG).toISOString(), device, user, coDrivers, SITUATIONAL_DRIVING_CLEAR, origin)
        ];
        if (includeStandardLog) {
            newLogs.push(this.createManualDutyStatusLog(endTime.toISOString(), device, user, coDrivers, status ?? (exemptionLog.status === "YM" ? "ON" : "OFF"), origin));
        }

        if (exemptionLog.status !== "WT") {
            const lastDrivingInExemptionLog = this.getLastLogIfDrivingWhileInExemption(user.id, exemptionLog, endTime.toISOString());
            const drivingStoppedTime = new Date(endTime.getTime() - DSWIE_DIFFERENCE_FROM_LOG).toISOString();
            if (lastDrivingInExemptionLog && drivingStoppedTime > lastDrivingInExemptionLog.dateTime) {
                newLogs.unshift(this.createManualDutyStatusLog(drivingStoppedTime, device, user, [], "DrivingStoppedWhileInExemption", "Automatic"));
            }
        }

        return newLogs;
    }

    getAdditionalLogsBeforeNewLogIfDrivingInExemption(newLogDateTime: Date, currentUser: IUser | undefined, skipSdc?: boolean) {
        const previousLog = getPreviousOrNextByDate(this.getAllHOSStatusLogsIncludingSDC(true, undefined, false), newLogDateTime, true);
        return this.getLogsToEndDrivingExemption(currentUser, previousLog, newLogDateTime, "Manual", undefined, false, skipSdc);
    }

    getAdditionalLogsBeforeInspectionOnLogIfDrivingInPc(newLogDateTime: Date, currentUser: IUser | undefined, latestLog: DutyStatusLog | null): DutyStatusLog[] {
        return latestLog?.status === "PC"
            ? this.getAdditionalLogsBeforeNewLogIfDrivingInExemption(newLogDateTime, currentUser, false)
            : [];
    }

    isReviewed(log: DutyStatusLog) {
        return log.state !== "Requested" && log.state !== "Transitioning";
    }

    getGroupedExemptionLogEdits(logs?: DutyStatusLog[], exemptionLogsIdsToResolve?: string[], isAccepted = true): IExemptionLogEditRequest[] {
        let logIdsToResolve = exemptionLogsIdsToResolve;
        if (logIdsToResolve && !logIdsToResolve.length) {
            return [];
        }

        const isExemptionLogStart = (log: DutyStatusLog, editedExemptionLog: DutyStatusLog | null) => {
            return ((!logIdsToResolve || !!editedExemptionLog) && log.isEditableExemptionLog()) || logIdsToResolve?.some(logId => logId === log.id);
        };
        const isValidRequestedAdd = (log: DutyStatusLog, rangeStartLog: DutyStatusLog) => {
            return log.requestedAdd && HOS_STANDARD_STATUSES.indexOf(log.status) > -1 && log.getDeviceId() === rangeStartLog.getDeviceId();
        };
        const shouldRemoveIntPc = (exemptionLog: DutyStatusLog, log: DutyStatusLog) => exemptionLog.status === "PC" && log.status === "INT_PC";
        const groupedLogsToResolve: IExemptionLogEditRequest[] = [];
        let currentRequestGroup: IExemptionLogEditRequest = {
            editedExemptionLog: null,
            logsToEdit: [],
            logsToRemove: []
        };

        const logCollection = logs ?? this.getAllHOSStatusLogsIncludingSDC();
        for (const log of logCollection) {
            if (isExemptionLogStart(log, currentRequestGroup.editedExemptionLog)) {
                if (!currentRequestGroup.editedExemptionLog) {
                    currentRequestGroup.editedExemptionLog = log;
                    continue;
                }

                // Support for multiple of the same exemption log in one range
                if (log.status === currentRequestGroup.editedExemptionLog.status && log.device.id === currentRequestGroup.editedExemptionLog.device.id) {
                    currentRequestGroup.logsToRemove.push(log);
                    continue;
                }
            }

            if (currentRequestGroup.editedExemptionLog) {
                if (isValidRequestedAdd(log, currentRequestGroup.editedExemptionLog)) {
                    currentRequestGroup.logsToEdit.push(log);
                    continue;
                }

                if (shouldRemoveIntPc(currentRequestGroup.editedExemptionLog, log)) {
                    currentRequestGroup.logsToRemove.push(log);
                    continue;
                }

                const isSituationalDrivingClear = log.status === SITUATIONAL_DRIVING_CLEAR;
                if (isSituationalDrivingClear || ALL_HOS_STATUSES.indexOf(log.status) > -1) {
                    const currentExemptionLog = currentRequestGroup.editedExemptionLog;
                    const exemptionLogEditRequest = currentExemptionLog?.requestedEdit?.isRequestedRemoval
                        ? currentExemptionLog
                        : undefined;
                    const sdcEditRequest = isSituationalDrivingClear && log.requestedEdit ? log : undefined;

                    const editRequestToProcess = exemptionLogEditRequest || sdcEditRequest;
                    if (editRequestToProcess) {
                        if (editRequestToProcess.status === SITUATIONAL_DRIVING_CLEAR) {
                            this.processExemptionShortening(currentRequestGroup, editRequestToProcess);
                        } else {
                            this.processExemptionRemoval(currentRequestGroup, editRequestToProcess, log);
                        }

                        // Don't want to remove any logs if we are rejecting
                        if (!isAccepted) {
                            currentRequestGroup.logsToRemove = [];
                        }
                        groupedLogsToResolve.push(currentRequestGroup);
                    }

                    // Remove the current exemption log from the list to resolve
                    if (logIdsToResolve) {
                        logIdsToResolve = logIdsToResolve.filter(logId => logId !== currentExemptionLog?.id);
                        if (!logIdsToResolve.length) {
                            break;
                        }
                    }

                    // Previous period complete, start a new exemption period
                    currentRequestGroup = {
                        editedExemptionLog: log.isEditableExemptionLog() ? log : null,
                        logsToEdit: [],
                        logsToRemove: []
                    };
                }
            }
        }

        return groupedLogsToResolve;
    }

    private processExemptionShortening(
        currentRequestGroup: IExemptionLogEditRequest,
        sdcLog: DutyStatusLog
    ): void {
        // When shortening, we're moving the SDC earlier, so the SDC becomes the "edited exemption log"
        currentRequestGroup.editedExemptionLog = sdcLog;

        // Remove all logs that occur after the new (earlier) SDC time
        const newExemptionEndDate = sdcLog.requestedEdit?.dateTime;
        if (newExemptionEndDate) {
            currentRequestGroup.logsToRemove = currentRequestGroup.logsToRemove.filter(
                removedLog => removedLog.dateTime >= newExemptionEndDate
            );
        }
    }

    private processExemptionRemoval(
        currentRequestGroup: IExemptionLogEditRequest,
        exemptionEditRequest: DutyStatusLog,
        endLog: DutyStatusLog
    ): void {
        // When removing, the exemption log itself becomes the "edited exemption log"
        currentRequestGroup.editedExemptionLog = exemptionEditRequest;

        // If the boundary log for the range is an SDC, it needs to be removed if the exemption is being removed
        if (endLog.status === SITUATIONAL_DRIVING_CLEAR) {
            currentRequestGroup.logsToRemove.push(endLog);
        }
    }

    private getUserId(overrideUser: string | undefined) {
        return overrideUser ?? this.userService.getCurrentUser()?.id;
    }

    private convertToDictionary(logs: DeepReadonlyObject<IIdDictionary<DutyStatusLog>>) {
        let logsDictionary: IIdDictionary<DutyStatusLog> = {};
        if (logs) {
            Object.keys(logs).forEach(key => {
                if (logs) {
                    logsDictionary[key] = new DutyStatusLog(logs[key]);
                }
            });
        }

        return logsDictionary;
    }
}
